import { useState } from 'react';
import { Link } from 'react-router-dom';

import { renderMarkdown } from '../../../common/components/markdown/markdown';
import { Tag } from '../../../common/components/detail/detail';
import { formatDateTime } from '../../../core/utils/date';
import { useAuthorNames } from '../../../global/contexts/lookups/lookups';
import { useTaskRounds } from '../contexts/reviews';
import { FINDING_LABEL } from '../utils/delivery';

import '../styles/queue.css';
import '../styles/history.css';

// EL HISTORIAL DE REVISIONES DE UNA TAREA: todas sus rondas, no solo la que
// está sobre la mesa.
//
// Una entrega abre una ronda, y la ronda FIJA LA VERSIÓN que se evalúa
// (`base_commit` → `delivered_commit`) junto con los criterios tal como estaban
// ese día. Por eso una tarea devuelta tres veces son tres rondas y no una
// corregida: cada una conserva contra qué se midió.
//
// LO QUE ESTA PANTALLA NO COLAPSA:
//
//   · evaluar no es decidir — la evaluación escribe dictámenes criterio por
//     criterio y deja la ronda abierta; decidir es de una persona con QA;
//   · `no_comprobado` no es «cumple» — se cuenta aparte, siempre;
//   · una ronda sin versión o sin criterios fijados se dice, no se rellena con
//     los criterios de hoy, que son otros.
//
// SOLO LEE. Decidir se hace en la cola de revisión, que es donde alguien está
// drenando trabajo; ofrecer el botón acá sería un segundo camino a la misma
// escritura, con sus propias maneras de equivocarse.

const ROUND_STATE = {
    open:     { label: 'Esperando decisión', tone: 'accent' },
    approved: { label: 'Aprobada',           tone: 'active' },
    returned: { label: 'Devuelta',           tone: 'warn' },
};

export default function ReviewHistory({ taskId, fallbackCriteria = [], legacyReviews = [] }) {
    const { rounds, isLoading, error } = useTaskRounds(taskId);
    const names = useAuthorNames();

    if (isLoading) return <p className="delivery__muted">Cargando las rondas de revisión...</p>;

    if (error) {
        return (
            <p className="review-history__error">
                {error.message || 'No se pudieron leer las rondas de revisión de esta tarea.'}
            </p>
        );
    }

    if (!rounds.length) {
        return (
            <section className="review-history">
                <p className="delivery__muted">
                    Esta tarea todavía no tiene rondas de revisión: ninguna entrega abrió una.
                </p>
                {/* Una tarea anterior al modelo de rondas igual tiene veredictos,
                    y son lo único que hay de ella. */}
                {legacyReviews.length > 0 && <LegacyReviews reviews={legacyReviews} names={names} />}
            </section>
        );
    }

    const decided = rounds.filter(round => round.state !== 'open').length;

    return (
        <section className="review-history">
            <p className="review-history__lead">
                {rounds.length} {rounds.length === 1 ? 'entrega' : 'entregas'}, {decided}{' '}
                {decided === 1 ? 'decidida' : 'decididas'}. Cada ronda aprueba o devuelve{' '}
                <strong>la versión que esa entrega fijó</strong>, no la tarea para siempre.
            </p>

            <ol className="review-history__rounds">
                {rounds.map((round, index) => (
                    <Round
                        key={round.id}
                        round={round}
                        names={names}
                        fallbackCriteria={fallbackCriteria}
                        /* La primera abierta, el resto plegado: un historial
                           largo que se abre entero es un muro de texto. */
                        startOpen={index === 0}
                    />
                ))}
            </ol>

            {legacyReviews.length > 0 && <LegacyReviews reviews={legacyReviews} names={names} />}
        </section>
    );
}

function Round({ round, names, fallbackCriteria, startOpen }) {
    const [open, setOpen] = useState(startOpen);
    const state = ROUND_STATE[round.state] ?? { label: round.state, tone: 'neutral' };
    const panelId = `round-${round.id}`;

    return (
        <li className={`review-round review-round--${round.state}`}>
            <header className="review-round__head">
                <button
                    type="button"
                    className="review-round__toggle"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    aria-controls={panelId}
                >
                    <span className="review-round__number">Ronda {round.number}</span>
                    <Tag tone={state.tone}>{state.label}</Tag>
                    {round.selfApproved && (
                        <Tag
                            tone="warn"
                            title="Quien decidió es quien entregó. Queda registrado y no se presenta como una revisión independiente."
                        >
                            autoaprobada
                        </Tag>
                    )}
                    {!round.versioned && <Tag tone="warn">sin versión</Tag>}
                    <span className="review-round__chevron" aria-hidden="true">{open ? '−' : '+'}</span>
                </button>
            </header>

            <p className="review-round__stamps">
                <span>
                    Entregada el <strong>{formatDateTime(round.openedAt) ?? 'sin fecha'}</strong>
                    {round.deliveredBy != null && <> por {names.userName(round.deliveredBy)}</>}
                </span>
                {round.decidedAt
                    ? (
                        <span>
                            Decidida el <strong>{formatDateTime(round.decidedAt)}</strong>
                            {round.decidedBy != null && <> por {names.userName(round.decidedBy)}</>}
                            {round.decidedByAgent && ' · con una credencial de agente'}
                        </span>
                    )
                    : <span className="review-round__pending">Sin decidir</span>}
                {round.specRevision != null && (
                    <span>Especificación rev. {round.specRevision}</span>
                )}
            </p>

            {open && (
                <div className="review-round__body" id={panelId}>
                    <Version round={round} />
                    <Evaluation round={round} fallbackCriteria={fallbackCriteria} />
                    <Log entries={round.entries} names={names} />
                    <Commits commits={round.commits} />
                </div>
            )}
        </li>
    );
}

// La versión que la ronda fijó. Sin ella no hay contra qué medir, y Core no
// deja aprobarla: se dice como la ausencia que es.
function Version({ round }) {
    return (
        <div className="delivery__block">
            <h4 className="delivery__block-title">Versión evaluada</h4>
            {round.versioned
                ? (
                    <p className="review-round__version">
                        <Hash hash={round.baseCommit} label="base" />
                        <span aria-hidden="true">→</span>
                        <Hash hash={round.deliveredCommit} label="entregado" />
                    </p>
                )
                : (
                    <p className="delivery__blocked">
                        La entrega no fijó commit base ni entregado. Una ronda así no se puede
                        aprobar: no hay versión contra la cual medirla.
                    </p>
                )}
        </div>
    );
}

function Evaluation({ round, fallbackCriteria }) {
    const summary = round.summary;

    return (
        <div className="delivery__block">
            <h4 className="delivery__block-title">Criterios de esta entrega</h4>

            {summary && (
                <p className="delivery__summary">
                    <span className="delivery__result delivery__result--cumple">{summary.cumple ?? 0} cumple</span>
                    <span className="delivery__result delivery__result--incumple">{summary.incumple ?? 0} incumple</span>
                    <span className="delivery__result delivery__result--no_comprobado">
                        {summary.no_comprobado ?? 0} no comprobado
                    </span>
                    {summary.sin_evaluar != null && (
                        <span className="delivery__result">{summary.sin_evaluar} sin evaluar</span>
                    )}
                </p>
            )}

            {round.criteria
                ? (
                    <ol className="delivery__criteria-list">
                        {round.criteria.map(criterion => (
                            <li key={criterion.index} className="delivery__criterion">
                                <span className="delivery__criterion-text">{criterion.text}</span>
                                {criterion.finding
                                    ? (
                                        <span className={`delivery__result delivery__result--${criterion.finding.result}`}>
                                            {FINDING_LABEL[criterion.finding.result] ?? criterion.finding.result}
                                        </span>
                                    )
                                    : <span className="delivery__result">Sin evaluar</span>}
                                {criterion.finding?.reason && (
                                    <p className="delivery__finding-reason">{criterion.finding.reason}</p>
                                )}
                                {criterion.finding?.evidence && (
                                    <p className="delivery__finding-evidence">
                                        Evidencia: {criterion.finding.evidence}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>
                )
                : (
                    <>
                        <p className="delivery__blocked">
                            Esta ronda no fijó sus criterios: es anterior a esa instantánea. Abajo
                            van los criterios de HOY, que pueden no ser los que se evaluaron.
                        </p>
                        <ul className="delivery__criteria">
                            {fallbackCriteria.map((criterion, index) => <li key={index}>{criterion}</li>)}
                        </ul>
                    </>
                )}
        </div>
    );
}

// La bitácora: lo que escribió la evaluación y lo que escribió una persona, en
// el mismo hilo y diciendo cuál es cuál.
function Log({ entries, names }) {
    return (
        <div className="delivery__block">
            <h4 className="delivery__block-title">
                Bitácora {entries.length > 0 && <span className="delivery__muted">· {entries.length}</span>}
            </h4>

            {entries.length === 0
                ? <p className="delivery__muted">Nadie escribió en esta ronda.</p>
                : (
                    <ul className="delivery__log">
                        {entries.map(entry => (
                            <li key={entry.id} className={`delivery__entry delivery__entry--${entry.kind}`}>
                                <span className="delivery__stamp">
                                    <span className="delivery__entry-kind">
                                        {entry.kind === 'ai' ? 'IA' : 'Persona'}
                                    </span>
                                    {' '}
                                    {entry.tokenId != null
                                        ? names.tokenName(entry.tokenId)
                                        : (entry.authorName ?? names.userName(entry.authorId))}
                                    {entry.at && ` · ${formatDateTime(entry.at)}`}
                                </span>
                                <div className="md md--field">{renderMarkdown(entry.body)}</div>
                            </li>
                        ))}
                    </ul>
                )}
        </div>
    );
}

// Los commits de la tarea hasta esa entrega — no el rango de una rama.
function Commits({ commits }) {
    if (!commits.length) return null;

    return (
        <div className="delivery__block">
            <h4 className="delivery__block-title">Commits hasta esta entrega ({commits.length})</h4>
            <ul className="delivery__hashes">
                {commits.map(commit => (
                    <li key={commit.hash}>
                        <Hash hash={commit.hash} />
                        <span className="delivery__hash-message">{commit.message ?? 'Sin mensaje'}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Hash({ hash, label }) {
    if (!hash) return null;
    return (
        <Link
            to={`/admin/tasks/trace?hash=${hash}`}
            className="delivery__hash"
            title={label ? `${label}: ${hash} — ver de dónde salió` : `${hash} — ver de dónde salió`}
        >
            {String(hash).slice(0, 10)}
        </Link>
    );
}

// El modelo anterior a las rondas: un veredicto suelto, sin versión ni
// criterios. Se muestra aparte para que no se lea como una ronda incompleta.
function LegacyReviews({ reviews, names }) {
    return (
        <div className="delivery__block">
            <h4 className="delivery__block-title">Revisiones anteriores al modelo de rondas</h4>
            <ul className="delivery__log">
                {reviews.map(review => (
                    <li key={review.id} className="delivery__entry">
                        <span className="delivery__stamp">
                            <span className="delivery__entry-kind">
                                {review.result === 'accepted' ? 'Aceptada' : 'Devuelta'}
                            </span>
                            {' '}
                            {review.reviewer_name ?? names.userName(review.reviewer_id)}
                            {review.reviewed_at && ` · ${formatDateTime(review.reviewed_at)}`}
                        </span>
                        {review.reason && <p className="delivery__finding-reason">{review.reason}</p>}
                    </li>
                ))}
            </ul>
        </div>
    );
}
